// Finished-character mascot model (data-driven).
//
// Each pose renders exactly ONE complete, ready-made PNG — never cropped, stretched, repositioned,
// recolored, or layered/composited with other character features. The pose's `baseVariants[gender]`
// holds the DEFAULT finished character per gender; `mascot_characters` holds additional finished
// variants (tagged with poseId + gender). The generator draws the selected variant's single PNG.

// Resolve the base sub-layers for a gender (the pose's default character source).
export function baseLayersFor(pose, gender) {
  const bv = (pose && pose.baseVariants) || {};
  if (gender && Array.isArray(bv[gender]) && bv[gender].length) return bv[gender];
  const avail = ["female", "male"].filter((g) => Array.isArray(bv[g]) && bv[g].length);
  if (avail.length) return bv[avail[0]];
  return (pose && pose.layers) || [];
}

// The default finished-character PNG src for a pose + gender (from the pose base).
// Strict to the requested gender: no cross-gender fallback, so a gender without its own
// base art (e.g. male) shows only its registered finished characters (no leaked "Original").
export function defaultSrcFor(pose, gender) {
  const bv = (pose && pose.baseVariants) || {};
  const own = Array.isArray(bv[gender]) ? bv[gender] : [];
  const ls = own.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return (ls.find((l) => l.role === "body-base" || l.role === "base") || ls[0])?.src || null;
}

// Genders selectable for this pose: a base exists OR at least one enabled finished character exists.
export function availableGendersFor(pose, characters = []) {
  const bv = (pose && pose.baseVariants) || {};
  return ["female", "male"].filter((g) => {
    const hasBase = Array.isArray(bv[g]) && bv[g].length > 0;
    const hasChar = characters.some((c) => c.poseId === pose?.id && c.gender === g && c.enabled !== false);
    return hasBase || hasChar;
  });
}

// Ordered list of finished character variants for a pose + gender: the default (from the pose base)
// first, then any enabled `mascot_characters`. Each item: { id, name, src, isDefault }.
export function variantsFor(pose, gender, characters = []) {
  if (!pose) return [];
  const list = [];
  const def = defaultSrcFor(pose, gender);
  if (def) list.push({ id: "__default__", name: "Original", src: def, isDefault: true });
  characters
    .filter((c) => c.poseId === pose.id && c.gender === gender && c.enabled !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .forEach((c) => list.push({ id: c.id, name: c.name || "Variant", src: c.src, isDefault: !!c.isDefault }));
  // If a stored character is flagged default, surface it first (after the synthetic Original).
  return list;
}
